import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { audits, type AuditSummary, type AuditableLocation } from '../api/audits';
import { templates, type FormTemplateSummary } from '../api/templates';
import {
  IconArrowRight,
  IconCircleAlert,
  IconClipboardList,
  IconFileText,
  IconPlay,
  IconRefreshCw,
  IconTrash2,
  IconTriangleAlert,
} from '../icons';
import { formatDate } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Select } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { SyncChip } from '../ui/SyncChip';
import { Table, type Column } from '../ui/Table';
import { outbox, cache } from '../offline/store';
import { BandChip } from '../ui/BandChip';

/**
 * The auditor's own list, and the way into a new visit.
 *
 * <p>Picking a location comes first, and the checklists are then whatever applies there —
 * that is what the location type and the publication scope exist for. Showing every
 * published template and letting the auditor choose is the one place in the flow where
 * picking wrong is easy and expensive: the wrong checklist still produces a score, and the
 * score looks entirely ordinary.</p>
 */
export function Audits() {
  const t = useTranslate();
  const navigate = useNavigate();

  const [rows, setRows] = useState<AuditSummary[]>([]);
  const [locations, setLocations] = useState<AuditableLocation[]>([]);

  /** Whether this person may start a visit at all. False for a reader of audits. */
  const [canStart, setCanStart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [offered, setOffered] = useState<FormTemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [doomed, setDoomed] = useState<AuditSummary | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const mine = await audits.list({ mine: true });
      setRows(mine);
      setFailed(false);
      setError(null);

      // Asked for separately, and allowed to fail on its own. It needs audits.conduct,
      // which a reader of audits does not have — and the two used to be awaited together,
      // so a 403 on the list of places somebody could start a visit turned a page of
      // perfectly good visits into "could not load". The visits are the screen; starting
      // one is an action on it.
      try {
        setLocations(await audits.locations());
        setCanStart(true);
      } catch {
        setLocations([]);
        setCanStart(false);
      }
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The checklists are asked for as soon as a shop is picked, because the answer is the
  // whole point of having picked one.
  useEffect(() => {
    if (!locationId) {
      setOffered(null);
      return;
    }

    setOffered(null);
    setDrawerError(null);

    templates
      .forLocation(locationId)
      .then(setOffered)
      .catch((e) => {
        setOffered([]);
        setDrawerError(describeError(e, 'error.listFailed'));
      });
  }, [locationId]);

  async function start(templateId: string) {
    if (busy) return;

    setBusy(true);
    try {
      const fresh = await audits.start(locationId, templateId);
      navigate(`/denetimler/${fresh.summary.id}`);
    } catch (e) {
      setDrawerError(describeError(e, 'error.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Throws away a draft, and everything the device was still holding for it.
   *
   * <p>The local copy goes too. A queue still trying to put answers on an audit the server
   * has thrown away would report every one of them to the auditor as a failure, for a visit
   * they deliberately abandoned.</p>
   */
  async function discard(row: AuditSummary) {
    setBusy(true);
    try {
      await audits.discard(row.id);
      await outbox.forget(row.id);
      await cache.forget(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function openReport(id: string) {
    if (drawing) return;

    setDrawing(id);
    try {
      const { url } = await audits.report(id);

      // A new tab rather than a download: a PDF opens in the browser's own viewer, and
      // somebody who wants the file can save it from there. Opened from this click so the
      // popup blocker treats it as something the person asked for.
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(describeError(e, 'error.reportFailed'));
    } finally {
      setDrawing(null);
    }
  }

  const columns: Column<AuditSummary>[] = [
    {
      id: 'location',
      header: t('audits.location'),
      className: 'is-primary',
      cell: (row) => (
        <>
          <span className="nv-code">{row.locationCode}</span> {row.locationName}
          {row.reference ? <div className="nv-code nv-subtle">{row.reference}</div> : null}
        </>
      ),
      skeletonWidth: '68%',
    },
    {
      id: 'form',
      header: t('audits.form'),
      className: 'is-muted',
      cell: (row) => (
        <span className="nv-badge-group">
          {row.formName}
          <Badge tone="neutral" shape="square">{`v${row.formVersion}`}</Badge>
        </span>
      ),
      skeletonWidth: '60%',
    },
    {
      id: 'when',
      header: t('audits.when'),
      className: 'is-muted',
      cell: (row) => formatDate(row.startedAt),
      skeletonWidth: '82px',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <Badge tone={row.status === 'Draft' ? 'na' : 'comment'}>{t(`auditStatus.${row.status}`)}</Badge>
      ),
      skeletonWidth: '66px',
    },
    {
      id: 'score',
      header: t('audits.score'),
      align: 'right',
      cell: (row) =>
        row.score === null ? (
          <span className="nv-subtle">—</span>
        ) : (
          <span className="nv-badge-group nv-score-cell">
            <strong>{row.score}</strong>
            {row.band ? <BandChip band={row.band} colour={row.bandColour} /> : null}
            {row.zeroedByCritical ? (
              <Badge tone="critical" icon={IconTriangleAlert}>{t('audits.criticalFailed')}</Badge>
            ) : null}
          </span>
        ),
      skeletonWidth: '48px',
    },
    {
      id: 'report',
      header: '',
      align: 'right',
      // Only for a visit that has been handed in. A draft's report would be a document
      // about a half-finished audit, and the one thing worse than no report is one that
      // looks final and is not.
      cell: (row) =>
        row.status === 'Draft' ? null : (
          <IconButton
            label={t('audits.report')}
            icon={IconFileText}
            disabled={drawing === row.id}
            onClick={(e) => {
              // The row itself opens the audit; this is a different destination.
              e.stopPropagation();
              void openReport(row.id);
            }}
          />
        ),
      skeletonWidth: '24px',
    },
  ];

  return (
    <>
      <PageHeader
        crumbs={[t('app.field'), t('nav.audits')]}
        title={t('audits.title')}
        subtitle={t('audits.subtitle')}
        aside={<SyncChip />}
        action={
          // Offered only to somebody who may actually start one. A button that answers 403
          // is worse than no button: it reads as the product being broken rather than as
          // the job belonging to somebody else.
          canStart ? (
            <Button
              variant="primary"
              icon={IconPlay}
              onClick={() => {
                setLocationId('');
                setDrawerError(null);
                setStarting(true);
              }}
            >
              {t('audits.start')}
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('audits.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={(row) => navigate(`/denetimler/${row.id}`)}
          rowActions={(row) =>
            // Only a draft. A submitted visit has a reference and a score the location has
            // seen; the way back from one of those is an appeal, not a delete.
            row.status === 'Draft' ? (
              <RowMenu
                label={t('common.moreActions')}
                items={[
                  {
                    label: t('audits.discard'),
                    icon: IconTrash2,
                    danger: true,
                    disabled: busy,
                    onSelect: () => setDoomed(row),
                  },
                ]}
              />
            ) : null
          }
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconClipboardList}
              title={t('audits.empty')}
              text={t('audits.emptyText')}
              action={
                <Button variant="primary" icon={IconPlay} onClick={() => setStarting(true)}>
                  {t('audits.start')}
                </Button>
              }
            />
          }
        />
      </Card>

      <Drawer
        open={starting}
        title={t('audits.start')}
        onClose={() => setStarting(false)}
        footer={<Button onClick={() => setStarting(false)}>{t('common.cancel')}</Button>}
      >
        <Field
          label={t('audits.location')}
          hint={locations.length === 0 ? t('audits.noLocations') : undefined}
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">{t('common.none')}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{`${l.code} · ${l.name}`}</option>
              ))}
            </Select>
          )}
        </Field>

        {locationId ? (
          <div className="nv-drawer__section">
            <h3 className="nv-h3">{t('audits.whichForm')}</h3>
            <p className="nv-hint">{t('audits.whichFormHint')}</p>

            {offered === null ? <p className="nv-hint">{t('common.loading')}</p> : null}

            {offered?.length === 0 ? <Alert tone="warning">{t('audits.noneApply')}</Alert> : null}

            {offered?.map((template) => (
              <button
                key={template.id}
                type="button"
                className="nv-pick"
                disabled={busy}
                onClick={() => void start(template.id)}
              >
                <span className="nv-pick__text">
                  <span className="nv-pick__name">{template.name}</span>
                  <span className="nv-hint">
                    {`v${template.version} · ${template.questionCount} ${t('audits.questions')}`}
                  </span>
                </span>
                <IconArrowRight size={16} />
              </button>
            ))}
          </div>
        ) : null}

        {drawerError ? <Alert>{drawerError}</Alert> : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('audits.discardTitle', { location: doomed.locationCode }) : ''}
        confirmLabel={t('audits.discard')}
        onConfirm={() => (doomed ? discard(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        {doomed ? (
          <>
            <p>{`${doomed.locationName} · ${doomed.formName}`}</p>
            <p>{t('audits.discardWarning')}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
